import { Card, Flex, Tooltip, Typography } from "antd";
import { ArrowDownOutlined, ArrowUpOutlined, InfoCircleOutlined, UserAddOutlined } from "@ant-design/icons";
import { Column } from "@ant-design/plots";
import { agruparSerie, nombreDePeriodo, type DashboardPeriod } from "../../utils";
import dayjs from "dayjs";
import { estadoDeWidget, WidgetPlaceholder } from "../widget-state";
import type { WidgetDataProps } from "../../types";

const { Text } = Typography;

const CARD_HEAD   = 38;
const PAD_V       = 18;
const NUMBER_H    = 48;
const GAP_H       = 8;
const MIN_CHART_H = 50;


type DataPoint = { d: string; n: number };
type PeriodEntry = { data: DataPoint[]; prevTotal: number };


type Datos = {
  total: number;
  serie: { date: string; count: number }[];
  anterior: number | null;
};

export const NuevosContactosChip = ({
  height = 240,
  period = "day",
  data,
  estado,
  error,
  nuncaCalculado,
  recargar,
}: { height?: number; period?: DashboardPeriod } & WidgetDataProps<Datos>) => {
  const status = estadoDeWidget(estado, error, nuncaCalculado, (data?.total ?? 0) > 0, recargar);
  // El agregado es diario; la etiqueta del eje se arma aca segun el periodo.
  // La etiqueta lleva dos líneas: qué es la barra (Lun, Sem 1, Ene) y a qué fechas corresponde.
  const serie = agruparSerie(data?.serie ?? [], period).map(({ d, sub, n }) => ({
    d: sub ? `${d}\n${sub}` : d,
    n,
  }));
  const prevTotal  = data?.anterior ?? 0;
  const total      = data?.total ?? 0;
  const diff       = total - prevTotal;
  const up         = diff >= 0;
  const pct        = prevTotal > 0 ? Math.round((diff / prevTotal) * 100) : 0;
  const bodyH  = height - CARD_HEAD;
  const chartH = Math.max(MIN_CHART_H, bodyH - PAD_V - NUMBER_H - GAP_H);

  return (
    <Card
      size="small"
      style={{ height }}
      styles={{ body: { padding: "12px 16px 10px", height: bodyH, overflow: "hidden", display: "flex", flexDirection: "column" } }}
      title={
        <Flex align="center" gap={8}>
          <UserAddOutlined style={{ fontSize: 14 }} />
          <span style={{ fontWeight: 600 }}>Nuevos contactos</span>
          <Tooltip
            title="Contactos que entraron al sistema en el período. Refleja el ritmo de captación de oportunidades en la parte alta del funnel."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 240 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
      extra={<Text type="secondary" style={{ fontSize: 11 }}>{nombreDePeriodo(period)}</Text>}
    >
      {status.tipo !== "ok" ? (
        <WidgetPlaceholder status={status} height={bodyH - 24} />
      ) : (
        <>
      {/* Big metric */}
      <div style={{ flexShrink: 0, marginBottom: GAP_H }}>
        <Flex align="baseline" gap={8} style={{ marginBottom: 2 }}>
          <Text strong style={{ fontSize: 32, lineHeight: 1 }}>{total}</Text>
          <span style={{ fontSize: 11, color: up ? "#15744b" : "#cf1322", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 2 }}>
            {up ? <ArrowUpOutlined style={{ fontSize: 9 }} /> : <ArrowDownOutlined style={{ fontSize: 9 }} />}
            {up ? "+" : ""}{pct}% vs ant.
          </span>
        </Flex>
        <Text type="secondary" style={{ fontSize: 11 }}>contactos · anterior {prevTotal}</Text>
      </div>

      <div style={{ height: chartH, overflow: "hidden" }}>
        <Column
          data={serie}
          xField="d"
          yField="n"
          color="#15744b"
          columnStyle={{ fillOpacity: 0.18, stroke: "#15744b", lineWidth: 1.5 }}
          autoFit
          columnWidthRatio={0.6}
          xAxis={{
            // `autoHide: false` es el punto: con muchas barras G2 esconde etiquetas por su cuenta,
            // y era por eso que la referencia sólo se veía al pasar el mouse.
            label: { style: { fontSize: 10, lineHeight: 13, fill: "rgba(0,0,0,0.45)" }, autoHide: false, offset: 6 },
            line: null,
            tickLine: null,
          }}
          yAxis={{
            label: { style: { fontSize: 9, fill: "rgba(0,0,0,0.35)" } },
            grid: { line: { style: { stroke: "#f5f5f5" } } },
            tickCount: 4,
          }}
          tooltip={{
            formatter: (d: any) => ({ name: String(d.d).replace("\n", " · "), value: `${d.n} contactos` }),
            domStyles: {
              "g2-tooltip": { background: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", border: "none", borderRadius: "6px", padding: "8px 10px", color: "rgba(0,0,0,0.85)", fontSize: "12px" },
            },
          }}
          legend={false}
          padding={[8, 8, 46, 8]}
          animation={false}
        />
      </div>
            </>
      )}
    </Card>
  );
};
